/**
 * OutflowColumn - Renders the right column of expense category nodes
 *
 * Maps positioned outflow nodes to OutflowNode components.
 */

import { memo, useCallback } from 'react';
import type { OutflowNode as OutflowNodeType, PositionedNode, FlowNode } from './types';
import { isOutflowNode } from './types';
import { OutflowNode } from './nodes';

export interface OutflowColumnProps {
  positionedNodes: PositionedNode[];
  onNodeClick?: (node: FlowNode) => void;
  /** Called when mouse enters a node */
  onNodeHover?: (node: FlowNode | null, event?: React.MouseEvent) => void;
  /** ID of the currently highlighted node (if any) */
  highlightedNodeId?: string | null;
}

function OutflowColumnComponent({
  positionedNodes,
  onNodeClick,
  onNodeHover,
  highlightedNodeId,
}: OutflowColumnProps) {
  // Filter to only outflow nodes
  const outflowNodes = positionedNodes.filter(
    (p): p is PositionedNode & { node: OutflowNodeType } => isOutflowNode(p.node)
  );

  const handleMouseEnter = useCallback(
    (node: OutflowNodeType, event: React.MouseEvent) => {
      onNodeHover?.(node, event);
    },
    [onNodeHover]
  );

  const handleMouseLeave = useCallback(() => {
    onNodeHover?.(null);
  }, [onNodeHover]);

  if (outflowNodes.length === 0) {
    return null;
  }

  return (
    <g className="outflow-column">
      {outflowNodes.map((positioned) => (
        <OutflowNode
          key={positioned.node.id}
          node={positioned.node}
          x={positioned.x}
          y={positioned.y}
          width={positioned.width}
          height={positioned.height}
          onClick={onNodeClick as ((node: OutflowNodeType) => void) | undefined}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          isHighlighted={highlightedNodeId === positioned.node.id}
        />
      ))}
    </g>
  );
}

export const OutflowColumn = memo(OutflowColumnComponent);
export default OutflowColumn;
